import React, { ReactNode, RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { useHiddenSelect } from 'react-aria';
import { DistributiveOmit, genericMemo, JSONValue } from '@direct-frontend/stdlib';
import { Selection, SelectionMode } from '@react-types/shared';
import { getPlatformStyles, isDesktop, isMobile } from '@direct-frontend/platform-code';
import { useLogger } from '@direct-frontend/modules-logger';
import { BaseRowData, DataProviderRowData } from '@adv-frontend/data-provider';
import { Item } from 'react-stately';

import { Popup } from '../Popup';
import { cn } from '../../lib/css/className/className';
import { getDefaultRenderer } from '../ListBoxBase/Render/Render';
import { getDisabledKeys, getKeys } from '../ListBoxBase/utils';
import { getItemKey, isSection } from '../ListBoxBase/Item/utils';
import { processItems } from '../ListBox/utils';
import { ListBox, ListBoxOptionItem, ListBoxSectionItem } from '../ListBox';
import { Drawer, DrawerHeader } from '../Drawer';
import { isSetsAreEqual } from '../../lib/set/isSetsAreEqual/isSetsAreEqual';
import { VirtualListBox } from '../ListBox/VirtualListBox';
import { DrawerHeaderProps } from '../Drawer/Header/Header';

import { SelectTrigger } from './Trigger/Trigger';
import { MultiSelectState, MultiSelectStateProps, useMultiSelectState } from './hooks/useMultiSelectState';
import { useMultiSelect } from './hooks/useMultiSelect';
import { SelectProps, SelectWithItemsProps } from './types';
import {
    isMultiSelectProps,
    isMultiSelectPropsWithoutDataProvider,
    useAriaProps,
    useCommonSelectProps,
    usePinnedSections,
} from './utils';
import { TriggerProps } from './Trigger/types';

getPlatformStyles({
    common: import('./Select.css'),
    mobile: import('./Select@mobile.css'),
});

export const cnSelect = cn('Select');

const getRenderItems = <ValueType extends string, SectionType extends string = ValueType>() =>
    getDefaultRenderer<ListBoxOptionItem<ValueType>, ListBoxSectionItem<SectionType, ValueType>>(getItemKey);

const VirtualSelect = function VirtualSelect<RowData extends BaseRowData>(
    props: Exclude<SelectProps<string, string, RowData>, SelectWithItemsProps<string, string>>,
) {
    const {
        dataProvider,
        onOpen,
        onClose,
        logNode,
        size,
        children,
        width,
        triggerContentPlaceholder,
        disabledKeys,
        emptySection,
        matchTriggerWidth,
        'data-testid': dataTestid = 'Select',
    } = props;
    const { LogProvider, logClick } = useLogger('Select', logNode);
    const loadedRows = dataProvider.getLoadedRows();
    const isMultiSelect = isMultiSelectProps(props);
    const renderedItemsMap = useMemo(() => {
        const result = new Map<string, RowData>();
        for (const row of loadedRows) {
            result.set(row.id, row);
        }

        return result;
    }, [loadedRows]);
    const rows = dataProvider.getRows();

    const commonSelectProps = useCommonSelectProps({
        ...props,
        onOpen: () => {
            logClick({ type: 'onOpen' });
            selectedItemsBeforeOpen.current = state.selectionManager.selectedKeys;
            onOpen?.();
        },
        onClose: () => {
            logClick({ type: 'onClose' });
            onClose?.();
        },
    });

    const selectProps = {
        ...commonSelectProps,
        allowEmptyCollection: Boolean(props.pinnedSection || props.pinnedSectionAfter || props.emptySection),
        items: rows,
        disabledKeys,
        onSelectionChange: selection => {
            const values = Array.from(selection).map(String);
            const items = values.map(value => renderedItemsMap.get(value ?? ''));

            const logData: JSONValue = {
                type: 'onSelectionChange',
                value: isMultiSelect ? values : values[0],
            };
            logClick(logData);

            if (isMultiSelect) {
                props.onChange(values, items);
            } else if (values[0] && items[0]) {
                props.onChange(values[0], items[0]);
            }
        },
        children: item => <Item>{item.id}</Item>,
    } satisfies MultiSelectStateProps<DataProviderRowData>;

    const state = useMultiSelectState(selectProps);
    const { pinnedSection, pinnedSectionAfter } = usePinnedSections(props, state);
    const ref = useRef<HTMLButtonElement>(null);
    const { triggerProps: ariaTriggerProps } = useMultiSelect(selectProps, state, ref);

    const selectedItemsBeforeOpen = useRef(state.selectionManager.selectedKeys);
    const onCancelClick = useCallback(() => {
        state.selectionManager.setSelectedKeys(selectedItemsBeforeOpen.current);
        if (isMultiSelect) {
            state.close();
        }
    }, [isMultiSelect, state]);
    const onSaveClick =
        isMultiSelect &&
        !isSetsAreEqual(
            // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
            selectedItemsBeforeOpen.current,
            state.selectionManager.selectedKeys,
        )
            ? state.close
            : undefined;

    const selectedItems = Array.from(state.selectionManager.selectedKeys).reduce<ListBoxOptionItem[]>((res, key) => {
        const providerRow = renderedItemsMap.get(key.toString());

        let content = providerRow && 'dataProvider' in props && props.renderTriggerContent(providerRow);
        if (!content) {
            if (!triggerContentPlaceholder) {
                content = String(key);
            }
            if (triggerContentPlaceholder instanceof Function) {
                content = triggerContentPlaceholder(String(key));
            } else {
                content = triggerContentPlaceholder;
            }
        }

        if (content) {
            res.push({ value: String(key), content });
        }

        return res;
    }, []);

    const triggerProps = {
        ...ariaTriggerProps,
        value: selectedItems,
    };

    return (
        <LogProvider>
            <SelectWrapper
                {...props}
                state={state}
                triggerProps={triggerProps}
                triggerRef={ref}
                onCancelClick={onCancelClick}
                onSaveClick={onSaveClick}
            >
                <VirtualListBox
                    state={state}
                    size={size}
                    dataProvider={dataProvider}
                    pinnedSection={pinnedSection}
                    pinnedSectionAfter={pinnedSectionAfter}
                    emptySection={emptySection}
                    maxHeight={props.maxHeight}
                    width={isMobile() || matchTriggerWidth ? 'max' : width}
                    aria-label={props['aria-label']}
                    data-testid={`${dataTestid}.ListBox`}
                >
                    {children}
                </VirtualListBox>
            </SelectWrapper>
        </LogProvider>
    );
};

const ItemsSelect = function ItemsSelect<ValueType extends string, SectionType extends string = ValueType>(
    props: SelectWithItemsProps<ValueType, SectionType>,
) {
    const {
        items,
        value,
        onOpen,
        onClose,
        logNode,
        size,
        width,
        addonBeforeSection: propsAddonBeforeSection,
        addonBefore,
        addonAfter,
        displaySelection = true,
        onScroll,
        matchTriggerWidth,
        'data-testid': dataTestid = 'Select',
    } = props;

    const { LogProvider, logClick } = useLogger('Select', logNode);
    const selectionMode: SelectionMode = Array.isArray(value) ? 'multiple' : 'single';
    const commonSelectProps = useCommonSelectProps({
        ...props,
        onOpen: () => {
            logClick({ type: 'onOpen' });
            setSelectedItemsBeforeOpen(state.selectionManager.selectedKeys);
            onOpen?.();
        },
        onClose: () => {
            logClick({ type: 'onClose' });
            onClose?.();
        },
    });

    const selectProps = {
        ...commonSelectProps,
        allowEmptyCollection: Boolean(props.pinnedSection || props.pinnedSectionAfter),
        items: processItems({
            items,
            shouldAddAddon: Boolean(propsAddonBeforeSection),
        }),
        children: getRenderItems(),
        disabledKeys: getDisabledKeys<
            ValueType,
            SectionType,
            ListBoxOptionItem<ValueType>,
            ListBoxSectionItem<SectionType, ValueType>
        >(items, getItemKey),
        onSelectionChange: (selection: Selection) => {
            if (isMultiSelectPropsWithoutDataProvider(props)) {
                let value: ValueType[];

                if (selection === 'all') {
                    value = getKeys<
                        ValueType,
                        SectionType,
                        ListBoxOptionItem<ValueType>,
                        ListBoxSectionItem<SectionType, ValueType>
                    >(items, getItemKey);
                } else {
                    // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                    value = Array.from(selection) as ValueType[];
                }

                logClick({
                    type: 'onSelectionChange',
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- ValueType является подмножеством string, но TS не может вывести тип в дженерике JSONCompatible
                    value: value as string[],
                });

                return props.onChange(value);
            }

            // Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            const value = Array.from(selection)[0] as ValueType;

            const logData: JSONValue = { type: 'onSelectionChange', value };
            logClick(logData);

            return props.onChange(value);
        },
    };
    const state = useMultiSelectState(selectProps);
    const ref = useRef<HTMLButtonElement>(null);
    const { triggerProps: ariaTriggerProps, menuProps } = useMultiSelect(selectProps, state, ref);
    const { pinnedSection, pinnedSectionAfter, addonBeforeSection } = usePinnedSections(props, state);
    const [selectedItemsBeforeOpen, setSelectedItemsBeforeOpen] = useState(state.selectionManager.selectedKeys);
    const onCancelClick = useCallback(() => {
        state.selectionManager.setSelectedKeys(selectedItemsBeforeOpen);
        if (selectionMode === 'multiple') {
            state.close();
        }
    }, [selectedItemsBeforeOpen, selectionMode, state]);
    const onSaveClick =
        selectionMode === 'multiple' && !isSetsAreEqual(selectedItemsBeforeOpen, state.selectionManager.selectedKeys)
            ? state.close
            : undefined;

    const selectedItems = Array.from(state.selectionManager.selectedKeys).reduce<ListBoxOptionItem<ValueType>[]>(
        (res, key) => {
            const item = state.collection.getItem(key);

            if (item?.value && !isSection(item.value)) {
                res.push(item.value);
            }

            return res;
        },
        [],
    );

    const triggerProps = {
        ...ariaTriggerProps,
        value: selectedItems,
    };

    return (
        <LogProvider>
            <SelectWrapper
                {...props}
                state={state}
                triggerProps={triggerProps}
                triggerRef={ref}
                onCancelClick={onCancelClick}
                onSaveClick={onSaveClick}
            >
                <ListBox
                    {...menuProps}
                    items={items}
                    selectionMode={selectionMode}
                    state={state}
                    size={size}
                    className={cnSelect('listbox')}
                    addonBefore={addonBefore}
                    addonAfter={addonAfter}
                    pinnedSection={pinnedSection}
                    pinnedSectionAfter={pinnedSectionAfter}
                    addonBeforeSection={addonBeforeSection}
                    data-testid={`${dataTestid}.ListBox`}
                    displaySelection={displaySelection}
                    width={isMobile() || matchTriggerWidth ? 'max' : width}
                    shouldSelectOnPressUp={false}
                    onScroll={onScroll}
                    preventOverscroll
                />
            </SelectWrapper>
        </LogProvider>
    );
};

type SelectWrapperProps<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
    StateType extends object = object,
> = DistributiveOmit<SelectProps<ValueType, SectionType, RowData>, 'children'> &
    Pick<DrawerHeaderProps, 'onCancelClick' | 'onSaveClick'> & {
        state: MultiSelectState<StateType>;
        children: ReactNode;
        triggerProps: Partial<TriggerProps<ValueType>> & Required<Pick<TriggerProps<ValueType>, 'value'>>;
        triggerRef: RefObject<HTMLElement>;
        isFullscreen?: boolean;
    };

const HIDDEN_SELECT_FALLBACK = {
    type: 'item',
    key: 0,
    value: null,
    level: 0,
    hasChildNodes: false,
    childNodes: [],
    rendered: null,
    textValue: '',
};

const SelectWrapper = function SelectWrapper<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
    StateType extends object = object,
>(props: SelectWrapperProps<ValueType, SectionType, RowData, StateType>) {
    const {
        state,
        placement = 'bottom-start',
        maxHeight,
        flip,
        width = 'auto',
        children,
        label,
        className,
        value,
        size,
        color,
        placeholder,
        isDisabled,
        isTruncated,
        showCaption,
        validationState: selectState,
        triggerProps,
        onCancelClick,
        onSaveClick,
        triggerRef,
        'data-testid': dataTestid = 'Select',
        matchTriggerWidth,
        id,
        isFullscreen,
    } = props;

    const wrapperRef = useRef<HTMLDivElement>(null);
    // Используем заглушку в полях label, selectedItem, selectedKey, setSelectedKey,
    // т.к. useHiddenSelect рассчитан на селект с единственным выбранным элементом,
    // и inputProps, containerProps не используют для своих значений эти поля
    const { containerProps } = useHiddenSelect(
        { label: '', isDisabled },
        {
            ...state,
            selectedItem: state.collection.getItem(0) ?? HIDDEN_SELECT_FALLBACK,
            selectedKey: 0,
            setSelectedKey: () => {},
        },
        triggerRef,
    );
    const Trigger = props.renderTrigger ?? SelectTrigger;
    const ariaProps = useAriaProps(props);

    const wrappedListbox = isDesktop() ? (
        <Popup
            triggerRef={triggerRef}
            isOpen={state.isOpen}
            onClose={state.close}
            placement={placement}
            maxHeight={maxHeight}
            flip={flip}
            matchTriggerWidth={width === 'max' || matchTriggerWidth}
            data-testid={`${dataTestid}.Popup`}
        >
            {children}
        </Popup>
    ) : (
        <Drawer
            isOpen={state.isOpen}
            onClose={onCancelClick}
            isFullscreen={isFullscreen}
            className={cnSelect('drawer', { virtualized: 'dataProvider' in props })}
            header={
                <DrawerHeader
                    onCancelClick={onCancelClick}
                    title={label}
                    onSaveClick={onSaveClick}
                    data-testid={`${dataTestid}.Header`}
                />
            }
            data-testid={`${dataTestid}.Drawer`}
        >
            {children}
        </Drawer>
    );

    return (
        <>
            <div
                className={cnSelect('', [cnSelect('wrapper', { width }), className])}
                ref={isDesktop() ? wrapperRef : undefined}
                id={id}
            >
                <div {...containerProps}>
                    <input
                        tabIndex={-1}
                        data-test-value={Array.isArray(value) ? value.join(',') : value}
                        data-testid={`${dataTestid}.HiddenInput`}
                    />
                </div>
                <Trigger
                    data-testid={dataTestid}
                    {...triggerProps}
                    size={size}
                    color={color}
                    ref={triggerRef}
                    placeholder={placeholder}
                    isOpened={state.isOpen}
                    validationState={selectState}
                    isDisabled={isDisabled}
                    width={'max'}
                    isTruncated={isTruncated}
                    showCaption={showCaption}
                    // ariaProps должны идти в конце, чтобы переписать схожие свойства на корректные
                    {...ariaProps}
                />
            </div>
            {wrappedListbox}
        </>
    );
};

/**
 * Компонент для выбора элемента из списка
 */
export const Select = genericMemo(function Select<
    ValueType extends string,
    SectionType extends string = ValueType,
    RowData extends BaseRowData = BaseRowData,
>(props: SelectProps<ValueType, SectionType, RowData>) {
    return 'dataProvider' in props ? <VirtualSelect<RowData> {...props} /> : <ItemsSelect {...props} />;
});
